import React, { useMemo, useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import { addDaysUtc, todayKST } from '../../../utils/date';
import { BillingRuleFormModal } from './BillingRuleFormModal';
import { fmtDateRange, fmtMoney, fmtNum } from './monthlyCalculationUtils';

const S = {
  card: { border: '1px solid var(--border)', borderRadius: 12, background: 'var(--bg-card)', overflow: 'hidden' },
  head: { padding: '12px 14px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start', background: 'var(--bg-subtle)' },
  title: { margin: 0, fontSize: 14, fontWeight: 950, color: 'var(--text)' },
  sub: { marginTop: 3, fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.4 },
  body: { padding: 12, display: 'flex', flexDirection: 'column', gap: 12 },
  assignment: { border: '1px solid var(--border)', borderRadius: 12, background: 'var(--bg-subtle)', padding: 11 },
  top: { display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start', marginBottom: 10 },
  name: { fontSize: 13, fontWeight: 950, color: 'var(--text)' },
  mini: { fontSize: 11, color: 'var(--text-muted)', marginTop: 3, lineHeight: 1.4 },
  timeline: { position: 'relative', display: 'grid', gap: 8 },
  item: (tone) => ({
    position: 'relative',
    border: tone === 'locked' ? '1px solid rgba(100,116,139,0.25)' : tone === 'future' ? '1px solid rgba(37,99,235,0.28)' : '1px solid var(--border)',
    borderRadius: 11,
    padding: 10,
    background: tone === 'future' ? 'rgba(37,99,235,0.06)' : 'var(--bg-card)',
  }),
  itemHead: { display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' },
  date: { fontSize: 12, fontWeight: 950, color: 'var(--text)' },
  chips: { display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 7 },
  chip: (tone = 'default') => ({
    display: 'inline-flex',
    alignItems: 'center',
    minHeight: 20,
    borderRadius: 999,
    padding: '2px 8px',
    fontSize: 10,
    fontWeight: 950,
    background: tone === 'locked' ? 'rgba(100,116,139,0.14)' : tone === 'blue' ? 'rgba(37,99,235,0.12)' : tone === 'off' ? 'rgba(100,116,139,0.10)' : 'rgba(22,163,74,0.12)',
    color: tone === 'locked' ? '#64748b' : tone === 'blue' ? '#2563eb' : tone === 'off' ? '#64748b' : '#15803d',
  }),
  btn: { minHeight: 40, border: '1px solid var(--border)', borderRadius: 8, padding: '0 10px', background: 'var(--bg-card)', color: 'var(--text-sub)', fontSize: 11, fontWeight: 900, cursor: 'pointer', whiteSpace: 'nowrap' },
  primary: { minHeight: 40, border: 'none', borderRadius: 8, padding: '0 12px', background: '#2563eb', color: '#fff', fontSize: 12, fontWeight: 950, cursor: 'pointer', whiteSpace: 'nowrap' },
  error: { border: '1px solid rgba(220,38,38,0.2)', borderRadius: 8, padding: 8, background: 'rgba(220,38,38,0.08)', color: '#dc2626', fontSize: 12, fontWeight: 850 },
};

function incentiveSource(rule) {
  return rule?.incentive_source || (rule?.incentive_enabled === false ? 'none' : 'direct');
}

function incentiveChip(rule) {
  const source = incentiveSource(rule);
  if (source === 'none') return { tone: 'off', text: '인센티브 제외' };
  if (source === 'incentive_total') return { tone: 'blue', text: '인센티브 관리 금액' };
  const included = Number(rule.incentive_base_included_count || 0);
  const prefix = included > 0 ? `${fmtNum(included)}장 포함 · ` : '';
  return { tone: 'default', text: `${prefix}초과/차감 ${fmtMoney(rule.incentive_unit_price)}/장` };
}

function sortedRules(assignment) {
  return [...(assignment.billing_rules || [])].sort((a, b) => (
    String(a.effective_from || '').localeCompare(String(b.effective_from || '')) || Number(a.id || 0) - Number(b.id || 0)
  ));
}

function isLockedRule(rule, lockedThrough) {
  if (!lockedThrough) return false;
  return String(rule.effective_from || '') <= lockedThrough;
}

function minYmd(a, b) {
  if (!a) return b || null;
  if (!b) return a || null;
  return String(a) < String(b) ? String(a) : String(b);
}

function ruleDisplayEnd(rule, assignment) {
  const ruleEnd = rule.effective_to || null;
  const assignmentEnd = assignment?.end_date?.slice(0, 10) || null;
  return minYmd(ruleEnd, assignmentEnd);
}

function statusForRule(rule, lockedThrough, today, assignment) {
  if (isLockedRule(rule, lockedThrough)) return { tone: 'locked', label: '확정월 기준' };
  const displayEnd = ruleDisplayEnd(rule, assignment);
  if (displayEnd && displayEnd < today) return { tone: 'off', label: '종료' };
  if (String(rule.effective_from || '') > today) return { tone: 'blue', label: '예약' };
  if (!displayEnd || String(displayEnd) >= today) return { tone: 'default', label: '현재 적용' };
  return { tone: 'off', label: '종료' };
}

function defaultScheduleDate(assignment) {
  const lockedThrough = assignment.condition_locked_through;
  return lockedThrough ? addDaysUtc(lockedThrough, 1) : (assignment.start_date?.slice(0, 10) || todayKST());
}

function templateRuleFor(assignment, date) {
  const rules = sortedRules(assignment);
  return [...rules].reverse().find((rule) => (
    String(rule.effective_from || '') <= date && (!rule.effective_to || String(rule.effective_to) >= date)
  )) || rules[rules.length - 1] || null;
}

function isAssignmentEnded(assignment, today) {
  const end = assignment?.end_date?.slice(0, 10) || null;
  return Boolean(end && end < today);
}

function assignmentPeriodText(assignment) {
  return fmtDateRange(assignment.start_date?.slice(0, 10), assignment.end_date?.slice(0, 10) || '진행중');
}

function withIntegratedMenu(url, integratedMenuId) {
  if (!integratedMenuId) return url;
  const joiner = url.includes('?') ? '&' : '?';
  return `${url}${joiner}integrated_menu_id=${encodeURIComponent(integratedMenuId)}`;
}

// title·sub: 관리자 '급여 조건' 창이 쉬운 말로 넘긴다(2026-10-06). 마케터 정산서 상세는 넘기지 않아 종전 글자 그대로.
export function BillingRuleManager({
  assignments, integratedMenuId = null, onChanged, readOnly = false,
  title = '계산 조건 이력', sub = '조건은 배정 기간 안에서만 적용됩니다. 배정이 끝나면 조건도 종료일까지로 표시됩니다.',
}) {
  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const today = useMemo(() => todayKST(), []);

  const rows = useMemo(() => (
    [...(assignments || [])].sort((a, b) => (
      String(a.marketer_name || '').localeCompare(String(b.marketer_name || ''), 'ko')
      || String(a.consultant_name || '').localeCompare(String(b.consultant_name || ''), 'ko')
    ))
  ), [assignments]);

  function openSchedule(assignment) {
    const effectiveFrom = defaultScheduleDate(assignment);
    setModal({
      mode: 'schedule',
      assignment,
      rule: null,
      templateRule: templateRuleFor(assignment, effectiveFrom),
      defaultEffectiveFrom: effectiveFrom,
    });
    setError('');
  }

  function openEdit(assignment, rule) {
    setModal({ mode: 'edit', assignment, rule });
    setError('');
  }

  async function save(payload) {
    if (!modal?.assignment) return;
    setSaving(true);
    setError('');
    const body = integratedMenuId ? { ...payload, integrated_menu_id: integratedMenuId } : payload;
    const res = modal.mode === 'schedule'
      ? await apiClient.post(withIntegratedMenu(`/api/admin/marketer-consultant/assignments/${modal.assignment.id}/billing-rules/schedule`, integratedMenuId), body)
      : await apiClient.put(withIntegratedMenu(`/api/admin/marketer-consultant/billing-rules/${modal.rule.id}`, integratedMenuId), body);
    setSaving(false);
    if (!res.ok) {
      setError(res.data?.message || '계산 조건 저장에 실패했습니다.');
      return;
    }
    setModal(null);
    onChanged?.();
  }

  async function removeRule(rule) {
    if (!window.confirm('이 계산 조건을 삭제할까요?')) return;
    setSaving(true);
    const res = await apiClient.delete(withIntegratedMenu(`/api/admin/marketer-consultant/billing-rules/${rule.id}`, integratedMenuId));
    setSaving(false);
    if (!res.ok) {
      setError(res.data?.message || '계산 조건 삭제에 실패했습니다.');
      return;
    }
    setModal(null);
    onChanged?.();
  }

  return (
    <div style={S.card}>
      <div style={S.head}>
        <div>
          <h2 style={S.title}>{title}</h2>
          <div style={S.sub}>{sub}</div>
        </div>
      </div>
      <div style={S.body}>
        {error && <div style={S.error}>{error}</div>}
        {!rows.length && <div style={S.mini}>선택된 배정이 없습니다.</div>}
        {rows.map((assignment) => {
          const rules = sortedRules(assignment);
          const lockedThrough = assignment.condition_locked_through;
          const ended = isAssignmentEnded(assignment, today);
          return (
            <div key={assignment.id} style={S.assignment}>
              <div style={S.top}>
                <div>
                  <div style={S.name}>{assignment.marketer_name} → {assignment.consultant_name}</div>
                  <div style={S.mini}>
                    배정 기간: {assignmentPeriodText(assignment)}
                    {lockedThrough ? ` · ${lockedThrough}까지 확정 보존` : ''}
                    {ended ? ' · 종료된 배정' : ''}
                  </div>
                </div>
                {!readOnly && !ended && (
                  <button type="button" style={S.primary} onClick={() => openSchedule(assignment)}>새 조건 예약</button>
                )}
              </div>
              {!readOnly && ended && (
                <div style={S.mini}>종료된 배정은 새 조건을 예약하지 않습니다. 과거 조건 확인만 가능합니다.</div>
              )}
              <div style={S.timeline}>
                {rules.map((rule) => {
                  const status = statusForRule(rule, lockedThrough, today, assignment);
                  const locked = isLockedRule(rule, lockedThrough);
                  const excluded = !rule.salary_enabled && incentiveSource(rule) === 'none';
                  const incentive = incentiveChip(rule);
                  const displayEnd = ruleDisplayEnd(rule, assignment);
                  return (
                    <div key={rule.id} style={S.item(locked ? 'locked' : status.label === '예약' ? 'future' : 'default')}>
                      <div style={S.itemHead}>
                        <div>
                          <div style={S.date}>조건 적용: {fmtDateRange(rule.effective_from, displayEnd || '진행중')}</div>
                          {locked && <div style={S.mini}>확정된 정산에 사용된 조건이라 직접 수정하지 않습니다.</div>}
                          {displayEnd && !rule.effective_to && assignment.end_date && (
                            <div style={S.mini}>배정 종료일에 맞춰 표시했습니다.</div>
                          )}
                        </div>
                        {!readOnly && !locked && <button type="button" style={S.btn} onClick={() => openEdit(assignment, rule)}>수정</button>}
                      </div>
                      <div style={S.chips}>
                        <span style={S.chip(status.tone)}>{status.label}</span>
                        {excluded && <span style={S.chip('off')}>계산 제외</span>}
                        <span style={S.chip(rule.salary_enabled ? 'blue' : 'off')}>급여 {rule.salary_enabled ? fmtMoney(rule.monthly_salary) : '제외'}</span>
                        <span style={S.chip(incentive.tone)}>{incentive.text}</span>
                        <span style={S.chip(rule.leave_deduction_enabled ? 'default' : 'off')}>연차 {rule.leave_deduction_enabled ? `${fmtNum(rule.free_leave_days)}일 무료` : '제외'}</span>
                      </div>
                      {rule.notes && <div style={S.mini}>{rule.notes}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {!readOnly && modal && (
        <BillingRuleFormModal
          mode={modal.mode}
          assignment={modal.assignment}
          rule={modal.rule}
          templateRule={modal.templateRule}
          defaultEffectiveFrom={modal.defaultEffectiveFrom}
          lockedThrough={modal.assignment?.condition_locked_through || null}
          saving={saving}
          error={error}
          onSave={save}
          onDelete={removeRule}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
